import { Box } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { showV2CompactToast, showV2Toast } from "#app/notifications";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { BulkActionBar } from "#components/molecules/BulkActionBar/BulkActionBar";
import { BulkActionBarOverlay } from "#components/molecules/BulkActionBar/BulkActionBarOverlay";
import { Table, type TableHandle } from "#components/organisms/Table/Table";
import {
  getOnboardingProcessStepsQueryData,
  setOnboardingProcessStepsQueryData,
} from "#modules/onboardingV2/infra/onboardingProcessSteps.query";
import { type UpdateOnboardingProcessStepDto } from "#modules/onboardingV2/infra/updateOnboardingProcessStep.api";
import { useDeletableMaterialRows } from "#modules/onboardingV2/view/materialSpecification/useDeletableMaterialRows";
import { OnboardingV2Shell } from "#modules/onboardingV2/view/OnboardingV2Shell";
import { AddProcessStepModal } from "#modules/onboardingV2/view/processStepConfiguration/AddProcessStepModal";
import { DurationModal } from "#modules/onboardingV2/view/processStepConfiguration/DurationModal";
import { ProcessStepConfigurationEmptyState } from "#modules/onboardingV2/view/processStepConfiguration/ProcessStepConfigurationEmptyState";
import { useBulkDeleteOnboardingProcessSteps } from "#modules/onboardingV2/view/processStepConfiguration/useBulkDeleteOnboardingProcessSteps";
import { useDeleteOnboardingProcessStep } from "#modules/onboardingV2/view/processStepConfiguration/useDeleteOnboardingProcessStep";
import { useOnboardingProcessSteps } from "#modules/onboardingV2/view/processStepConfiguration/useOnboardingProcessSteps";
import {
  type DurationModalTarget,
  useProcessStepConfigurationColumns,
} from "#modules/onboardingV2/view/processStepConfiguration/useProcessStepConfigurationColumns";
import { useUpdateOnboardingProcessStep } from "#modules/onboardingV2/view/processStepConfiguration/useUpdateOnboardingProcessStep";
import {
  useOnboardingAnalytics,
  useTrackOnboardingStepViewed,
} from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { useOnboardingStepNavigation } from "#modules/onboardingV2/view/useOnboardingStepNavigation";
import { useUndoableBulkDelete } from "#modules/onboardingV2/view/useUndoableBulkDelete";
import { Icon } from "#shared/icons/Icon";
import { AnalyticsEvent } from "#shared/useAnalytics";
import {
  findDuplicateProcessStepIds,
  getMissingProcessStepFields,
  type ProcessStepFieldKey,
} from "shared";

export const OnboardingProcessStepConfigurationStepPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { processSteps, isLoading } = useOnboardingProcessSteps();
  const { updateOnboardingProcessStep } = useUpdateOnboardingProcessStep();
  const { deleteOnboardingProcessStep } = useDeleteOnboardingProcessStep();
  const { bulkDeleteOnboardingProcessSteps } = useBulkDeleteOnboardingProcessSteps();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasCheckedValidation, setHasCheckedValidation] = useState(false);
  const tableRef = useRef<TableHandle>(null);
  const [isAddModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [durationModalTarget, setDurationModalTarget] = useState<DurationModalTarget | null>(null);

  useTrackOnboardingStepViewed("processStepConfiguration");
  const { captureOnboardingEvent } = useOnboardingAnalytics("processStepConfiguration");

  const handleOpenAddModal = () => {
    captureOnboardingEvent(AnalyticsEvent.OnboardingAddProcessStepClicked);
    openAddModal();
  };

  const handleClearSelection = () => {
    tableRef.current?.resetRowSelection();
    setSelectedIds([]);
  };

  const { bulkDelete, commitPendingBulkDelete } = useUndoableBulkDelete({
    getRowId: (row) => row.id,
    getData: () => getOnboardingProcessStepsQueryData(queryClient),
    setData: (rows) => setOnboardingProcessStepsQueryData(queryClient, rows),
    onCommit: (ids) => bulkDeleteOnboardingProcessSteps(ids),
    showUndoToast: ({ id, count, onCancel }) =>
      showV2Toast({
        id,
        message: t("onboardingV2.processStepConfiguration.bulkDelete.toast", { count }),
        onCancel,
      }),
    onUndo: () => {
      handleClearSelection();
      showV2CompactToast({
        message: t("onboardingV2.processStepConfiguration.bulkDelete.undoToast"),
      });
    },
  });

  const { visibleRows, deleteRow, commitPendingRowDeletes } = useDeletableMaterialRows({
    rows: processSteps,
    getRowId: (processStep) => processStep.id,
    getDeleteToastMessage: (count) =>
      t("onboardingV2.processStepConfiguration.deleteToast.message", { count }),
    getUndoToastMessage: (count) =>
      t("onboardingV2.processStepConfiguration.undoToast.message", { count }),
    onConfirmDelete: (processStep) => deleteOnboardingProcessStep({ id: processStep.id }),
    onConfirmBulkDelete: (rows) =>
      bulkDeleteOnboardingProcessSteps(rows.map((processStep) => processStep.id)),
  });

  const { position, total, goToNext, goToPrevious } = useOnboardingStepNavigation(
    "processStepConfiguration",
    { hasProcessSteps: visibleRows.length > 0 },
  );

  const handleUpdateProcessStep = (id: string, payload: UpdateOnboardingProcessStepDto) => {
    updateOnboardingProcessStep({ id, payload });
  };

  const getMissingFieldRowIds = (rows: typeof visibleRows) =>
    rows.filter((row) => getMissingProcessStepFields(row).length > 0).map((row) => row.id);

  const duplicateRowIds = useMemo(
    () =>
      hasCheckedValidation ? new Set(findDuplicateProcessStepIds(visibleRows)) : new Set<string>(),
    [hasCheckedValidation, visibleRows],
  );
  const missingFieldRowIds = useMemo(
    () => (hasCheckedValidation ? new Set(getMissingFieldRowIds(visibleRows)) : new Set<string>()),
    [hasCheckedValidation, visibleRows],
  );
  const hasDuplicates = duplicateRowIds.size > 0;
  const hasMissingFields = missingFieldRowIds.size > 0;
  const erroredRowCount = useMemo(
    () => new Set([...duplicateRowIds, ...missingFieldRowIds]).size,
    [duplicateRowIds, missingFieldRowIds],
  );

  const columns = useProcessStepConfigurationColumns(
    handleUpdateProcessStep,
    deleteRow,
    setDurationModalTarget,
    hasCheckedValidation,
  );

  const handleBulkDelete = () => {
    bulkDelete(selectedIds);
    setSelectedIds([]);
  };

  const handleContinue = async () => {
    if (!hasCheckedValidation) {
      const hasDuplicatesNow = findDuplicateProcessStepIds(visibleRows).length > 0;
      const hasMissingFieldsNow = getMissingFieldRowIds(visibleRows).length > 0;

      if (hasDuplicatesNow || hasMissingFieldsNow) {
        setHasCheckedValidation(true);
        return;
      }
    } else if (hasDuplicates || hasMissingFields) {
      return;
    }

    commitPendingBulkDelete();
    commitPendingRowDeletes();
    goToNext();
  };

  return (
    <OnboardingV2Shell size="lg">
      <OnboardingV2Shell.Header
        onBack={goToPrevious}
        step={position}
        total={total}
        title={t("onboardingV2.processStepConfiguration.title")}
        subtitle={t("onboardingV2.processStepConfiguration.subtitle")}
      />

      <Box
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <OnboardingV2Shell.Content>
          <Table
            ref={tableRef}
            data={visibleRows}
            columns={columns}
            getRowId={(row) => row.id}
            getRowError={(row) => hasCheckedValidation && duplicateRowIds.has(row.id)}
            getCellError={(row, columnId) =>
              hasCheckedValidation &&
              getMissingProcessStepFields(row).includes(columnId as ProcessStepFieldKey)
            }
            getRowHeight={() => spacing[16]}
            loading={isLoading}
            emptyState={
              <ProcessStepConfigurationEmptyState onAddProcessStep={handleOpenAddModal} />
            }
            onRowSelectionChange={setSelectedIds}
          />
        </OnboardingV2Shell.Content>
        <BulkActionBarOverlay visible={selectedIds.length > 0}>
          <BulkActionBar
            label={t("onboardingV2.processStepConfiguration.bulkDelete.selectedCount", {
              count: selectedIds.length,
            })}
            clearAriaLabel={t("onboardingV2.processStepConfiguration.bulkDelete.clearSelection")}
            onClear={handleClearSelection}
            actions={[
              {
                key: "delete",
                icon: <Icon name="trash" size={16} />,
                label: t("common.delete"),
                onClick: handleBulkDelete,
              },
            ]}
          />
        </BulkActionBarOverlay>
      </Box>

      <OnboardingV2Shell.Footer
        buttonLabel={t("onboardingV2.continue")}
        onContinue={handleContinue}
        errorMessage={
          hasCheckedValidation && (hasMissingFields || hasDuplicates)
            ? hasMissingFields && hasDuplicates
              ? t("onboardingV2.processStepConfiguration.combinedValidationError", {
                  count: erroredRowCount,
                })
              : hasDuplicates
                ? t("onboardingV2.processStepConfiguration.duplicateError")
                : t("onboardingV2.processStepConfiguration.requiredFieldsError")
            : undefined
        }
        stepper={{ total, current: position }}
        leftAction={
          <Button
            variant="secondary"
            size="md"
            leftSection={<Icon name="plus" size={16} />}
            onClick={handleOpenAddModal}
          >
            {t("onboardingV2.processStepConfiguration.addProcessStep")}
          </Button>
        }
      />

      <AddProcessStepModal opened={isAddModalOpened} onClose={closeAddModal} />

      <DurationModal
        opened={durationModalTarget !== null}
        title={durationModalTarget?.title ?? ""}
        value={durationModalTarget?.value ?? null}
        onClose={() => setDurationModalTarget(null)}
        onSave={(value) => {
          if (durationModalTarget) {
            handleUpdateProcessStep(
              durationModalTarget.id,
              durationModalTarget.field === "preparationTimeFormula"
                ? { preparationTimeFormula: value }
                : { cycleTimeFormula: value },
            );
          }
          setDurationModalTarget(null);
        }}
      />
    </OnboardingV2Shell>
  );
};
